// --- Dependencies -------------------------------------------------------------------------------
import { useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyFile } from "@/types";
import Icon from "@/UI/atoms/Icon";
import FileInput from "@/UI/inputs/FileInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyReplaceFileDetails = { file: TyFile };

type Props = { onClose: () => void; details?: unknown };

/**
 * New bytes on the same file. Everything pointing at it keeps pointing at it — which is why this
 * exists rather than "delete and upload again", and why a member may do it although deleting is for
 * owners and managers. The previous version is not kept: keeping it means paying for it.
 *
 * Like every upload it hands off to the tray and closes; the old file keeps serving until the new one
 * has landed, so a failed replacement loses nothing.
 */
export default function ReplaceFilePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { file } = details as TyReplaceFileDetails;
    const enqueueReplace = useUploads((state) => state.enqueueReplace);

    const [files, setFiles] = useState<File[]>([]);
    const [formError, setFormError] = useState<string | null>(null);

    const handleSubmit = () => {
        setFormError(null);
        if (!files.length) return setFormError(t.drive.pick);
        enqueueReplace(file._id, files[0]);
        onClose();
    };

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.drive.actions.replace}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="side-form__hint">{file.name}</p>

                <FileInput files={files} onChange={setFiles} multiple={false} />

                {formError && <p className="side-form__error">{formError}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit}>
                    {t.drive.actions.replace}
                </button>
            </footer>
        </section>
    );
}
